import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getProjectById, createProject, updateProject, updateProjectStatus } from '../../api/pmsApi';

import CommonDatePicker from "../../components/DatePicker/CommonDatePicker.js";
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar.js';
import tiptapExtensions from '../../components/editor/tiptapExtensions.js';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop.js';
import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect.js';
import FileAttachment from '../../components/FileAttachment/FileAttachment.js';

function ProjectForm({ isInline, onCancelInline, onSuccessInline, calculatedProgress }) {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);

  // 상위 요청에서 넘어온 파라미터 (하향식 프로젝트 생성 시)
  const queryParams = new URLSearchParams(location.search);
  const instructionIdParam = queryParams.get('instruction_id');
  const titleParam = queryParams.get('title');
  const reviewerIdParam = queryParams.get('reviewer_id');
  const reviewerNameParam = queryParams.get('reviewer_name');
  const leaderNameParam = queryParams.get('leader_name');

  const [title, setTitle] = useState(titleParam || '');
  const [status, setStatus] = useState('');
  const [budget, setBudget] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [progressRate, setProgressRate] = useState(0);

  // 조직도 매핑 (Reviewer 단수, Leader 단수, Members 복수)
  const [reviewer, setReviewer] = useState(null); // 검토자(최종 승인권자)
  const [leader, setLeader] = useState(null);     // 책임자(PM)
  const [members, setMembers] = useState([]);     // 담당자 목록
  const [watchers, setWatchers] = useState([]);   // 참조자 목록
  const [authorName, setAuthorName] = useState(''); // 작성자 이름
  const [createdAt, setCreatedAt] = useState(null); // 작성일시

  // 조직도 모달 제어용 상태
  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const [orgTarget, setOrgTarget] = useState(''); // 'reviewer', 'leader', 'members'

  const [useReviewer, setUseReviewer] = useState(!!instructionIdParam); // 검토자 사용 여부 스위치

  // 파일 관리
  const [files, setFiles] = useState([]);

  const editor = useEditorWithDragDrop(tiptapExtensions);

  useEffect(() => {
    // 하향식일 경우, 본인을 자동으로 리더로 세팅
    if (!isEdit && user) {
      const myFullName = `${user.name || ''}${user.position ? ' / ' + user.position : ''}${user.dept_name ? '(' + user.dept_name + ')' : ''}`;
      setLeader({
        target_id: String(user.id),
        disp_target_name: leaderNameParam || myFullName || user.userid
      });
      setAuthorName(myFullName || user.userid);
      // 요청 기반인 경우 요청자를 검토자(최종 승인권자)로 자동 세팅
      if (reviewerIdParam) {
        setReviewer({
          target_id: String(reviewerIdParam),
          disp_target_name: reviewerNameParam || `ID:${reviewerIdParam}`
        });
      }
    }

    if (isEdit && id && token && editor) {
      getProjectById(id, token).then((res) => {
        if (res.ok) {
          const data = res.data;
          setTitle(data.title || '');
          setBudget(data.budget || '');
          if (data.start_date) setStartDate(new Date(data.start_date));
          if (data.end_date) setEndDate(new Date(data.end_date));
          if (calculatedProgress !== undefined && calculatedProgress !== null) {
            setProgressRate(calculatedProgress);
          } else if (data.progress_rate !== undefined) {
            setProgressRate(data.progress_rate);
          }
          if (data.status) setStatus(data.status);
          if (data.content) editor.commands.setContent(data.content);

          if (data.author) {
            setAuthorName(`${data.author.name || ''}${data.author.org?.position ? ' / ' + data.author.org.position : ''}${data.author.org?.dept_name ? '(' + data.author.org.dept_name + ')' : ''}`);
          }
          if (data.created_at) {
            setCreatedAt(data.created_at);
          }
          if (data.reviewer_id) {
            const revObj = data.reviewer;
            const revFullName = revObj ? `${revObj.name || ''}${revObj.org?.position ? ' / ' + revObj.org.position : ''}${revObj.org?.dept_name ? '(' + revObj.org.dept_name + ')' : ''}` : `ID:${data.reviewer_id}`;
            setReviewer({ target_id: String(data.reviewer_id), disp_target_name: revFullName });
            setUseReviewer(true);
          }
          if (data.leader_id) {
            const ledObj = data.leader;
            const ledFullName = ledObj ? `${ledObj.name || ''}${ledObj.org?.position ? ' / ' + ledObj.org.position : ''}${ledObj.org?.dept_name ? '(' + ledObj.org.dept_name + ')' : ''}` : `ID:${data.leader_id}`;
            setLeader({ target_id: String(data.leader_id), disp_target_name: ledFullName });
          }
          if (data.members) {
            const rawMembers = [];
            const rawWatchers = [];
            data.members.forEach(m => {
              const u = m.user;
              const mName = u ? u.name : '알수없음';
              const mPos = u?.org?.position ? `/ ${u.org.position}` : '';
              const mDept = u?.org?.dept_name ? `(${u.org.dept_name})` : '';
              const obj = { target_id: String(u && u.userid ? u.userid : m.user_id), disp_target_name: `${mName} ${mPos}${mDept}` };
              if (m.is_manager) rawMembers.push(obj);
              else rawWatchers.push(obj);
            });
            // 기존 데이터에 중복이 있을 수 있으므로 담당자에 있는 사람은 참조자에서 빼기
            const memberIds = rawMembers.map(m => m.target_id);
            const filteredWatchers = rawWatchers.filter(w => !memberIds.includes(w.target_id));
            setMembers(rawMembers);
            setWatchers(filteredWatchers);
          }
          if (data.files) {
            setFiles(data.files.map(f => ({ ...f, isNew: false, isDeleted: false })));
          }
        }
      }).catch(err => {
        console.error('프로젝트 로드 실패:', err);
        alert('데이터를 불러오는데 실패했습니다.');
        navigate('/pms/projects');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, token, editor, navigate, user]);

  const handleSubmit = async (e, overrideStatus = null) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!title.trim()) return alert('제목을 입력해주세요.');
    if (!startDate || !endDate) return alert('시작일과 마감일을 선택해주세요.');
    if (endDate < startDate) return alert('마감일은 시작일보다 빠를 수 없습니다.');
    if (!leader) return alert('책임자(PM)를 1명 지정해주세요.');
    if (instructionIdParam && !reviewer) {
      return alert('요청 기반 프로젝트는 최종 승인자(검토자)를 반드시 지정해야 합니다.');
    }

    const formData = new FormData();
    formData.append('title', title);
    if (editor) formData.append('content', editor.getHTML());
    formData.append('budget', budget || 0);

    // 날짜 포맷
    const sd = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const ed = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    formData.append('start_date', sd);
    formData.append('end_date', ed);
    formData.append('progress_rate', progressRate);

    if (useReviewer && reviewer) {
      formData.append('reviewer_id', reviewer.target_id);
    } else {
      // 스위치가 꺼져있거나 검토자가 없으면 null 처리
      formData.append('reviewer_id', '');
    }
    formData.append('leader_id', leader.target_id);

    const memberIds = members.map(m => m.target_id);
    formData.append('memberIds', JSON.stringify(memberIds));

    const watcherIds = watchers.map(w => w.target_id);
    formData.append('watchers', JSON.stringify(watcherIds));

    if (instructionIdParam) {
      formData.append('instruction_id', instructionIdParam);
    }

    // 신규 파일 필터링 및 맵핑 (FileAttachment 컴포넌트 규격에 맞춤)
    const newFiles = files.filter(f => f.isNew && !f.isDeleted);
    newFiles.forEach(f => {
      if (f.file) formData.append('files', f.file);
    });

    // 삭제할 파일 ID 수집
    const deletedIds = files.filter(f => f.isDeleted && !f.isNew && f.id != null).map(f => f.id);
    if (deletedIds.length > 0) {
      formData.append('deletedFileIds', JSON.stringify(deletedIds));
    }

    try {
      if (isEdit) {
        await updateProject(id, formData, token);
        if (overrideStatus) {
          await updateProjectStatus(id, overrideStatus, '수정 후 검토 재요청', token);
          alert('성공적으로 검토 재요청이 완료되었습니다.');
        } else {
          alert('프로젝트가 성공적으로 수정되었습니다.');
        }
      } else {
        await createProject(formData, token);
        if (reviewer) {
          alert('성공적으로 검토 요청이 완료되었습니다.');
        } else {
          alert('프로젝트가 성공적으로 생성되었습니다.');
        }
      }
      if (isInline && onSuccessInline) {
        onSuccessInline();
      } else {
        if (isEdit && !overrideStatus) {
          navigate(-1);
        } else {
          let finalStatus = 'pending';
          if (isEdit) {
            finalStatus = overrideStatus || status || 'pending';
          } else {
            finalStatus = reviewer ? 'review' : 'pending';
          }

          if (finalStatus === 'review') navigate('/pms/projects/review');
          else if (finalStatus === 'rejected') navigate('/pms/projects/rejected');
          else if (finalStatus === 'completed') navigate('/pms/projects/completed');
          else if (finalStatus === 'canceled') navigate('/pms/projects/canceled');
          else if (finalStatus === 'ready' || finalStatus === 'pending') navigate('/pms/projects/ready');
          else navigate('/pms/projects');
        }
      }
    } catch (err) {
      console.error('저장 실패:', err);
      alert('저장에 실패했습니다. ' + (err.response?.data?.message || err.message));
    }
  };

  const handleOrgConfirm = (selected) => {
    if (orgTarget === 'reviewer') {
      if (selected.length > 0) setReviewer(selected[0]);
    } else if (orgTarget === 'leader') {
      if (selected.length > 0) setLeader(selected[0]);
    } else if (orgTarget === 'members') {
      setMembers(selected);
      // 담당자로 지정된 사람이 참조자 목록에 있다면 참조자에서 자동 제거
      const memberIds = selected.map(s => String(s.target_id));
      setWatchers(prev => prev.filter(w => !memberIds.includes(String(w.target_id))));
    } else if (orgTarget === 'watchers') {
      // 담당자에 이미 있는 사람은 참조자에서 제외
      const memberIds = members.map(m => String(m.target_id));
      const filtered = selected.filter(s => !memberIds.includes(String(s.target_id)));
      if (filtered.length !== selected.length) {
        alert('담당자로 이미 지정된 사용자는 참조자에서 자동 제외되었습니다.');
      }
      setWatchers(filtered);
    }
    setShowOrgPopup(false);
  };

  return (
    <div className="container-fluid p-4">
      <style>{`
        .light-placeholder::placeholder {
          color: #adb5bd !important;
          font-weight: 400;
        }
        /* 프로그레스 바 커스텀 CSS */
        .custom-progress-range::-webkit-slider-runnable-track {
          background: transparent !important;
        }
        .custom-progress-range::-moz-range-track {
          background: transparent !important;
        }
        /* PMS 달력 전용 스타일 격리 */
        .pms-datepicker-container .react-datepicker-wrapper {
          width: 100% !important;
        }
      `}</style>
      {/* 🚀 상단 헤더 */}
      {!isInline && (
        <div className="d-flex justify-content-between align-items-center mb-4 p-3 rounded shadow-sm border-0"
          style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
          <h4 className="mb-0 fw-bold">
            <i className="fas fa-folder-plus me-2"></i>{isEdit ? '실행 프로젝트 수정' : '신규 실행 프로젝트 등록'}
          </h4>
          <button className="btn btn-light fw-bold text-dark btn-sm" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-1"></i>뒤로가기
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row g-4">

          {/* 📝 좌측 주요 정보 (제목, 본문, 예산, 파일) */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                <h5 className="fw-bold text-dark"><i className="fas fa-list-alt me-2 text-primary"></i>기본 정보 및 산출물</h5>
              </div>
              <div className="card-body">
                <div className="row mb-3 align-items-end">
                  <div className="col-md-8">
                    <label className="form-label fw-bold"><span className="text-primary fw-bold me-2 fs-5">H</span>프로젝트 명 <span className="text-danger">*</span></label>
                    <input
                      type="text" className="form-control bg-light border-0 light-placeholder"
                      placeholder="프로젝트 제목을 입력하세요."
                      value={title} onChange={e => setTitle(e.target.value)} required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold"><i className="fas fa-coins text-primary fs-5 me-2"></i>예산 (원)</label>
                    <input
                      type="number" className="form-control bg-light border-0 light-placeholder"
                      placeholder="예: 5000000"
                      value={budget} onChange={e => setBudget(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold"><i className="fas fa-align-left text-primary fs-5 me-2"></i>프로젝트 상세 개요</label>
                  <div className="border rounded-3 bg-light">
                    {editor && <EditorToolbar editor={editor} />}
                    <div className="bg-white" style={{ minHeight: '300px' }}>
                      <EditorContent editor={editor} className="editor-content p-3" />
                    </div>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold"><i className="fas fa-paperclip me-2"></i>첨부파일 / 산출물</label>
                  <FileAttachment
                    files={files}
                    setFiles={setFiles}
                    mode="edit"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ⚙️ 우측 설정 (기간, 인원 할당) */}
          <div className="col-lg-4">

            {/* 설정 카드 */}
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                <h5 className="fw-bold text-dark"><i className="fas fa-cog me-2 text-success"></i>일정 및 역할 할당</h5>
              </div>
              <div className="card-body">

                {/* 작성자 정보 */}
                <div className="mb-4">
                  <label className="form-label fw-bold"><i className="fas fa-user-edit text-primary fs-5 me-2"></i>프로젝트 작성자</label>
                  <div className="fw-bold text-dark" style={{ marginLeft: '28px' }}>
                    {authorName || '정보 없음'}
                  </div>
                </div>

                {/* 작성일시 (수정 모드일 때만 노출) */}
                {isEdit && createdAt && (
                  <div className="mb-4">
                    <label className="form-label fw-bold"><i className="fas fa-clock text-primary fs-5 me-2"></i>작성일시</label>
                    <div className="text-secondary fw-bold" style={{ marginLeft: '28px' }}>
                      {(() => {
                        const d = new Date(createdAt);
                        if (isNaN(d.getTime())) return createdAt;
                        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                      })()}
                    </div>
                  </div>
                )}

                {/* 기간 설정 */}
                <div className="mb-4">
                  <label className="form-label fw-bold"><i className="fas fa-calendar-alt text-primary fs-5 me-2"></i>진행 기간 <span className="text-danger">*</span></label>
                  <div className="d-flex align-items-center gap-2 pms-datepicker-container">
                    <CommonDatePicker value={startDate} onChange={setStartDate} placeholder="시작일" showTime={false} />
                    <span className="fw-bold text-muted">~</span>
                    <CommonDatePicker value={endDate} onChange={setEndDate} placeholder="종료일" showTime={false} />
                  </div>
                </div>

                {/* 진척도 (수정시에만 노출, 직접 드래그로 변경 가능) */}
                {isEdit && (
                  <div className="mb-0 slide-down-fade">
                    <label className="form-label fw-bold"><i className="fas fa-chart-line text-primary fs-5 me-2"></i>현재 진척도</label>
                    <div className="d-flex align-items-center bg-light py-2 px-3 rounded-3 border">
                      <input
                        type="range"
                        className="form-range flex-grow-1 me-2 custom-progress-range opacity-50"
                        min="0"
                        max="100"
                        step="1"
                        value={progressRate}
                        disabled={true}
                        style={{
                          cursor: 'not-allowed',
                          background: `linear-gradient(to right, #0d6efd 0%, #0d6efd ${progressRate}%, #dee2e6 ${progressRate}%, #dee2e6 100%)`,
                          borderRadius: '8px',
                          height: '8px'
                        }}
                      />
                      <span className="fw-bolder text-secondary" style={{ fontSize: '0.95rem', minWidth: '40px', textAlign: 'right', whiteSpace: 'nowrap' }}>{progressRate}%</span>
                    </div>
                    <div className="text-muted fs-8 mt-1"><i className="fas fa-info-circle me-1"></i>프로젝트 전체 진척도는 세부항목의 평균으로 <b>자동 계산</b>됩니다.</div>
                  </div>
                )}

                <hr className="text-muted opacity-25 my-2" />

                {/* 검토자 (단일) */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-3">
                      <label className="form-label fw-bold mb-0">
                        <i className="fas fa-user-check text-primary fs-5 me-2"></i>검토자 (최종결재) {instructionIdParam && <span className="text-danger">*</span>}
                      </label>
                      <div className="form-check form-switch mb-0 d-flex align-items-center">
                        <input
                          className="form-check-input mt-0"
                          type="checkbox"
                          id="useReviewerSwitch"
                          checked={useReviewer}
                          disabled={!!instructionIdParam}
                          style={{ cursor: instructionIdParam ? 'not-allowed' : 'pointer' }}
                          onChange={(e) => {
                            setUseReviewer(e.target.checked);
                            if (!e.target.checked) setReviewer(null);
                          }}
                        />
                        <label className="form-check-label small fw-bold text-muted ms-2 mb-0" htmlFor="useReviewerSwitch" style={{ cursor: instructionIdParam ? 'not-allowed' : 'pointer', paddingTop: '2px' }}>
                          지정
                        </label>
                      </div>
                    </div>

                    <div>
                      {useReviewer && (
                        <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3"
                          onClick={() => { setOrgTarget('reviewer'); setShowOrgPopup(true); }}>
                          <i className="fas fa-search me-1"></i>선택
                        </button>
                      )}
                    </div>
                  </div>

                  {useReviewer && (
                    <div className="p-2 bg-light rounded-3 border border-dashed slide-down-fade">
                      {reviewer ? (
                        <div className="d-flex align-items-center gap-2 ms-1" title={reviewer.disp_target_name}>
                          <i className="fas fa-user text-secondary flex-shrink-0"></i>
                          <span className="text-dark" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {reviewer.disp_target_name}
                          </span>
                        </div>
                      ) : <span className="text-muted small ms-1">선택 안됨</span>}
                    </div>
                  )}
                </div>

                {/* 팀장 (단일) */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold mb-0"><i className="fas fa-user-tie text-primary fs-5 me-2"></i>책임자(PM) <span className="text-danger">*</span></label>
                    <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3"
                      onClick={() => { setOrgTarget('leader'); setShowOrgPopup(true); }}>
                      <i className="fas fa-search me-1"></i>선택
                    </button>
                  </div>
                  <div className="p-2 bg-light rounded-3 border border-dashed">
                    {leader ? (
                      <div className="d-flex align-items-center gap-2 ms-1" title={leader.disp_target_name}>
                        <i className="fas fa-user text-secondary flex-shrink-0"></i>
                        <span className="text-dark" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {leader.disp_target_name}
                        </span>
                      </div>
                    ) : <span className="text-muted small ms-1">선택 안됨</span>}
                  </div>
                </div>

                {/* 팀원 (복수) */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <label className="form-label fw-bold mb-0"><i className="fas fa-users text-primary fs-5 me-2"></i>담당자</label>
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3 ms-auto"
                      onClick={() => { setOrgTarget('members'); setShowOrgPopup(true); }}>
                      <i className="fas fa-plus me-1"></i>선택
                    </button>
                  </div>
                  <div className="p-3 bg-light rounded-3 min-vh-25 border border-dashed" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {members.length === 0 ? (
                      <div className="text-center text-muted py-2">
                        <p className="mb-0">조직도에서 참여인원을 선택해주세요.</p>
                      </div>
                    ) : (
                      <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {members.map(m => (
                          <span key={m.target_id} className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center justify-content-between"
                            style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }}
                            title={m.disp_target_name}>
                            <span className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                              <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.disp_target_name}
                              </span>
                            </span>
                            <button type="button" className="btn-close btn-close-sm ms-2 flex-shrink-0" style={{ fontSize: '0.5rem' }}
                              onClick={() => setMembers(members.filter(x => x.target_id !== m.target_id))}
                            ></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 참조자 (복수) */}
                <div className="mb-2">
                  <div className="d-flex align-items-center mb-2">
                    <label className="form-label fw-bold mb-0"><i className="fas fa-share-square text-info fs-5 me-2"></i>참조자</label>
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3 ms-auto"
                      onClick={() => { setOrgTarget('watchers'); setShowOrgPopup(true); }}>
                      <i className="fas fa-plus me-1"></i>선택
                    </button>
                  </div>
                  <div className="p-3 bg-light rounded-3 min-vh-25 border border-dashed" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {watchers.length === 0 ? (
                      <div className="text-center text-muted py-2">
                        <p className="mb-0">조직도에서 참조자를 선택해주세요.</p>
                      </div>
                    ) : (
                      <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {watchers.map(m => (
                          <span key={m.target_id} className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center justify-content-between"
                            style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }}
                            title={m.disp_target_name}>
                            <span className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                              <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.disp_target_name}
                              </span>
                            </span>
                            <button type="button" className="btn-close btn-close-sm ms-2 flex-shrink-0" style={{ fontSize: '0.5rem' }}
                              onClick={() => setWatchers(prev => prev.filter(x => x.target_id !== m.target_id))}></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <hr className="text-muted opacity-25 mt-3" />

                <div className="d-flex justify-content-end gap-2 pb-2">
                  {isEdit && status === 'rejected' && useReviewer && (
                    <button type="button" className="btn btn-warning px-2 fw-bold text-dark rounded-3 shadow-sm" onClick={(e) => handleSubmit(e, 'review')}>
                      <i className="fas fa-paper-plane me-1"></i>검토 재요청
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-2 fw-bold rounded-3 shadow-sm">
                    <i className="fas fa-save me-1"></i>{isEdit ? '수정 완료' : (useReviewer ? '검토 요청' : '프로젝트 생성')}
                  </button>
                  {isInline ? (
                    <button type="button" className="btn btn-secondary px-2 fw-bold text-white rounded-3 shadow-sm" onClick={onCancelInline}>
                      <i className="fas fa-times me-1"></i>취소
                    </button>
                  ) : (
                    <button type="button" className="btn btn-secondary px-2 fw-bold text-white rounded-3 shadow-sm" onClick={() => navigate('/pms/projects')}>
                      <i className="fas fa-list me-1"></i>목록
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </form>

      {/* 조직도 모달 */}
      {showOrgPopup && (
        <OrgTreeSelect
          onConfirm={handleOrgConfirm}
          onCancel={() => setShowOrgPopup(false)}
          selectionMode={(orgTarget === 'members' || orgTarget === 'watchers') ? 'multiple' : 'single'}
          targetType="user"
          initialSelected={
            orgTarget === 'reviewer' && reviewer ? [reviewer] :
              orgTarget === 'leader' && leader ? [leader] :
                orgTarget === 'members' ? members :
                  orgTarget === 'watchers' ? watchers : []
          }
        />
      )}
    </div>
  );
}

export default ProjectForm;
